/* eslint-disable no-underscore-dangle */

import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Inline } from '@styleseat/ui';
import { GoogleLogin } from '@react-oauth/google';
import { TouchableWithoutFeedback } from 'react-native-web';
import type { LayoutChangeEvent } from 'react-native-web';
import { TERMS_VERSIONS } from '../../../../modules/Terms';
import type { GoogleAuthResponse } from '../../../../store/CurrentUser.types';
import analytics from '../../../../modules/analytics';
import { clickable } from '../../../../styles';

type GoogleSignInButtonProps = {
  onAccountCreated: (success: boolean, userData?: any) => void;
};

export const GoogleSignInButton: React.FCWithChildren<GoogleSignInButtonProps> = ({
  onAccountCreated,
}) => {
  const dispatch = useDispatch();
  const parentRef = useRef();
  const [googleLoginButtonWidth, setGoogleLoginButtonWidth] = useState<number>(200);

  const trackClickAnalytics = () => {
    analytics.track('google_sso_button_clicked');
  };

  const trackFailGoogleSide = () => {
    analytics.track('google_sso_failed_on_google');
  };

  const doGoogleLogin = async (token: string): Promise<void> => {
    const userData: GoogleAuthResponse = await dispatch.user.loginWithGoogle({
      google_jwt: token,
      accepted_terms: [
        {
          accepted_version: TERMS_VERSIONS.eula,
          terms_type: 'eula',
        },
        {
          accepted_version: TERMS_VERSIONS.privacy_policy + 1,
          terms_type: 'privacy_policy',
        },
        {
          accepted_version: TERMS_VERSIONS.consumer_tos,
          terms_type: 'tos',
        },
      ],
    });
    if (userData.action === 'Signup') {
      onAccountCreated(true, userData);
    }
  };

  return (
    <TouchableWithoutFeedback
      onPress={trackClickAnalytics}
    >
      <Inline
        onLayout={
          (e: LayoutChangeEvent) => setGoogleLoginButtonWidth(e.nativeEvent.layout.width)
        }
        ref={parentRef}
        alignItems="center"
        justifyContent="center"
        testID="googleSsoButton_inline"
        style={clickable}
      >
        <GoogleLogin
          onSuccess={async credentialResponse => {
            await doGoogleLogin(credentialResponse.credential);
          }}
          onError={trackFailGoogleSide}
          theme="outline"
          logo_alignment="center"
          // We need to set the width because it's an unresponsive iframe
          width={String(googleLoginButtonWidth)}
          useOneTap={false}
          ux_mode="popup"
        />
      </Inline>
    </TouchableWithoutFeedback>
  );
};
